import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';

export const httpErrorReasons = {
  network:
    'The request could not reach the server. It may have been blocked or the connection failed. Please try again, or contact support if this keeps happening.',
  firewall: 'The request was blocked by our security firewall. Please contact support.',
  timeout: 'The request timed out. Please check your connection and try again.',
};

/**
 * Builds a user facing error message for a failed request, e.g. "Failed to save stock item: Failed to upload image for category".
 * Uses the reason returned by the API when there is one, otherwise describes the failure based on the status.
 */
export const getHttpErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof TimeoutError) {
    return `${fallback}. ${httpErrorReasons.timeout}`;
  }

  if (!(error instanceof HttpErrorResponse)) {
    return fallback;
  }

  // Status 0 means the browser couldn't read the response, e.g. no connection or a firewall response without CORS headers
  if (error.status === 0) {
    return `${fallback}. ${httpErrorReasons.network}`;
  }

  const serverMessage = getServerMessage(error.error);
  if (serverMessage) {
    return `${fallback}: ${serverMessage}`;
  }

  if (error.status === 403 && isCloudflareBlockPage(error.error)) {
    const rayId = error.headers?.get('cf-ray') ?? getCloudflareRayId(error.error);
    return `${fallback}. ${httpErrorReasons.firewall}${rayId ? ` (Ray ID: ${rayId})` : ''}`;
  }

  return `${fallback} (HTTP ${error.status}).`;
};

const getServerMessage = (body: unknown): string | undefined => {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const { errors, message } = body as { errors?: { errorMessage?: string }[]; message?: string };
  const errorMessages = Array.isArray(errors)
    ? errors.map((apiError) => cleanErrorMessage(apiError?.errorMessage)).filter((errorMessage) => !!errorMessage)
    : [];

  if (errorMessages.length) {
    return errorMessages.join('; ');
  }

  return typeof message === 'string' && message.trim() ? message.trim() : undefined;
};

// The API returns messages like "type=Exception, message=Failed to upload image", only the message part is meaningful to users
const cleanErrorMessage = (errorMessage?: string): string | undefined => {
  if (typeof errorMessage !== 'string' || !errorMessage.trim()) {
    return undefined;
  }
  const match = errorMessage.match(/(?:^|,\s*)message=(.*)$/s);
  return (match ? match[1] : errorMessage).trim();
};

const isCloudflareBlockPage = (body: unknown): body is string => typeof body === 'string' && /cloudflare/i.test(body);

const getCloudflareRayId = (body: string): string | undefined => body.match(/Ray ID:\s*(?:<[^>]+>\s*)*([0-9a-f]{16})/i)?.[1];
